import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { needsImageUpgrade } from "@/lib/applianceImageQuality";

// Bumped when the quality bar changes — forces a fresh sweep per browser.
const SESSION_KEY = "applianceImageBackfill:done:v2";
const MAX_PER_SESSION = 25;
const CONCURRENCY = 2;

interface Row {
  id: string;
  name: string | null;
  brand: string | null;
  model_number: string;
  category: string | null;
  image_url: string | null;
}

const extractBrand = (row: Row): string | null => {
  if (row.brand) return row.brand;
  if (!row.name) return null;
  const known = ["bosch", "miele", "aeg", "siemens", "electrolux", "samsung", "lg", "whirlpool", "beko", "bauknecht"];
  const lower = row.name.toLowerCase();
  for (const b of known) {
    if (lower.includes(b)) return b.charAt(0).toUpperCase() + b.slice(1);
  }
  return row.name.split(" ")[0] || null;
};

async function processOne(row: Row, userId: string) {
  const brand = extractBrand(row);
  if (!row.model_number?.trim() && !brand?.trim()) return;
  try {
    const { data, error } = await supabase.functions.invoke("product-image-search", {
      body: {
        modelNumber: row.model_number,
        brand,
        productType: (row.category || "").replace("-", " "),
      },
    });
    if (error || !data?.success || !data?.data?.url) return;
    const url: string = data.data.url;
    if (needsImageUpgrade(url)) return; // double-check the result
    await supabase
      .from("appliances")
      .update({ image_url: url })
      .eq("id", row.id)
      .eq("user_id", userId);
  } catch (e) {
    console.warn("Image backfill failed for", row.model_number, e);
  }
}

/**
 * Once per browser session, scan the user's appliances and refresh any
 * stored image_url that fails the strict quality bar. Rate-limited.
 */
export function useApplianceImageBackfill() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;

    let cancelled = false;
    const run = async () => {
      try {
        const { data, error } = await supabase
          .from("appliances")
          .select("id, name, brand, model_number, category, image_url")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(200);
        if (error || !data || cancelled) return;

        const candidates = data.filter((r) => needsImageUpgrade(r.image_url)).slice(0, MAX_PER_SESSION);
        if (candidates.length === 0) {
          sessionStorage.setItem(SESSION_KEY, "1");
          return;
        }

        for (let i = 0; i < candidates.length; i += CONCURRENCY) {
          if (cancelled) return;
          const batch = candidates.slice(i, i + CONCURRENCY);
          await Promise.all(batch.map((r) => processOne(r, user.id)));
        }
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch (e) {
        console.warn("Appliance image backfill aborted:", e);
      }
    };

    // Defer so it doesn't compete with first paint / nav.
    const t = setTimeout(run, 4000);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user]);
}
